import { CheckCircle, AlertCircle, TrendingUp, RefreshCw, Lock } from 'lucide-react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ApuracaoSNProvider } from '../ApuracaoSNProvider';
import { useApuracaoSN } from '@/contexts';
import { RBT12Card } from './RBT12Card';
import { AnexoAliquotaCard } from './AnexoAliquotaCard';
import { DASCard } from './DASCard';
import { MemoriaCalculo } from './MemoriaCalculo';
import { SegregacaoDetalhadaSection } from './SegregacaoDetalhada/SegregacaoDetalhadaSection';
import { SegregacaoLoading } from './SegregacaoLoading';
import { FecharAbrirCompetenciaButton } from './FecharAbrirCompetenciaButton';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: 'easeOut' as const, delay },
  }),
};

function ApuracaoSNContent() {
  const {
    dados,
    isLoading,
    error,
    buscar,
    recalcular,
    podeCalcular,
    calculado,
    situacaoCompetencia,
  } = useApuracaoSN();

  const mostrarLoading = calculado && isLoading;
  const mostrarConteudo = calculado && !isLoading && !error;
  const fechada = situacaoCompetencia === 'Fechado';

  return (
    <div className="min-h-screen bg-background">
      <main className="px-6 py-6 space-y-6">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between rounded-xl border bg-card px-6 py-4 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Apuração Simples Nacional</h2>
              <p className="text-xs text-muted-foreground">Competência: {dados.competencia}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {mostrarConteudo && fechada && (
              <Badge variant="outline" className="gap-1.5 text-muted-foreground">
                <Lock className="h-3.5 w-3.5" />
                Fechada
              </Badge>
            )}
            {mostrarConteudo && dados.status === 'validado' && (
              <Badge className="bg-green-500 hover:bg-green-500/90 text-white gap-1.5">
                <CheckCircle className="h-3.5 w-3.5" />
                Validado
              </Badge>
            )}
            {podeCalcular && <FecharAbrirCompetenciaButton />}
            {mostrarConteudo && (
              <Button
                size="sm"
                className="gap-2 bg-foreground text-background hover:bg-foreground/90"
                onClick={recalcular}
                disabled={fechada}
                title={fechada ? 'Abra o período para recalcular' : undefined}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Recalcular Apuração
              </Button>
            )}
          </div>
        </div>

        {/* Estado: sem filtros selecionados */}
        {!podeCalcular && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-muted/30 py-16 text-center">
            <p className="text-sm font-medium text-foreground">Nenhuma competência selecionada</p>
            <p className="text-xs text-muted-foreground mt-1">
              Selecione uma empresa e um período (mês/ano) no cabeçalho para calcular a segregação.
            </p>
          </div>
        )}

        {/* Estado: filtros prontos mas ainda não buscou */}
        {podeCalcular && !calculado && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-muted/30 py-16 text-center">
            <p className="text-sm font-medium text-foreground">Pronto para calcular</p>
            <p className="text-xs text-muted-foreground mt-1">
              Empresa e período selecionados. Clique em <strong>Buscar</strong> no cabeçalho para
              iniciar o cálculo.
            </p>
          </div>
        )}

        {/* Loading animado */}
        {mostrarLoading && <SegregacaoLoading />}

        {/* Estado de erro */}
        {calculado && !isLoading && error && (
          <div className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-6 py-4">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">Erro ao carregar segregação</p>
              <p className="text-xs text-muted-foreground">
                Verifique se empresa e período estão selecionados no cabeçalho.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={buscar}>
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Conteúdo principal — só renderiza com dados */}
        <AnimatePresence>
          {mostrarConteudo && (
            <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 items-start">
              {/* Coluna esquerda — cards de visão geral */}
              <div className="space-y-4">
                <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible">
                  <RBT12Card />
                </motion.div>
                <motion.div custom={0.07} variants={fadeUp} initial="hidden" animate="visible">
                  <AnexoAliquotaCard />
                </motion.div>
                <motion.div custom={0.14} variants={fadeUp} initial="hidden" animate="visible">
                  <DASCard />
                </motion.div>
              </div>

              {/* Coluna direita — memória + segregação detalhada */}
              <div className="space-y-6">
                <motion.div custom={0.05} variants={fadeUp} initial="hidden" animate="visible">
                  <MemoriaCalculo />
                </motion.div>
                <motion.div custom={0.12} variants={fadeUp} initial="hidden" animate="visible">
                  <SegregacaoDetalhadaSection />
                </motion.div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

export function ApuracaoSNPage() {
  return (
    <ApuracaoSNProvider>
      <ApuracaoSNContent />
    </ApuracaoSNProvider>
  );
}
